/** Quote-scoped raw transport. Default chat authentication remains in chat.ts.
 * A run owns an actor/session, never the mounted drawer. Credential renewal can
 * resume its existing stream after /me; a failed stream is never auto-POSTed.
 */
import { tryRefreshToken } from "./client";
import {
  captureQuoteReadCredential,
  quoteReadCredentialCurrent,
  verifyQuoteReadIdentity,
  type QuoteReadCredential,
  type QuoteReadIdentity,
} from "./quoteReadIdentity";

export type QuoteChatContext = { kind: "factor_quote"; id: string };
export type QuoteChatScope = {
  readonly identity: QuoteReadIdentity;
  readonly sessionId: string;
  readonly owner: object;
  /** Actor/run lifetime; credential readiness is independently verified here. */
  readonly owns: () => boolean;
};
export class QuoteChatOwnershipError extends Error {
  constructor() {
    super("报价对话的登录身份或对象已变化，请重新确认");
    this.name = "QuoteChatOwnershipError";
  }
}
type Verification = {
  key: string;
  status: "checking" | "ready" | "blocked" | "error";
  promise: Promise<void>;
};
let verification: Verification | undefined;
const listeners = new Set<() => void>();
const publish = () => {
  for (const fn of listeners) fn();
};
const credentialKey = () =>
  JSON.stringify([
    captureQuoteReadCredential(),
    localStorage.getItem("cnc.refresh_token"),
  ]);
const key = (scope: QuoteChatScope) =>
  JSON.stringify([
    scope.identity.tenantId,
    scope.identity.userId,
    scope.identity.role,
    credentialKey(),
  ]);
const assertOwner = (scope: QuoteChatScope, signal?: AbortSignal) => {
  if (!scope.owns() || signal?.aborted) throw new QuoteChatOwnershipError();
};
export function quoteChatScopeCurrent(scope: QuoteChatScope): boolean {
  return (
    scope.owns() &&
    verification?.key === key(scope) &&
    verification.status === "ready"
  );
}
export function subscribeQuoteChatVerification(
  listener: () => void,
): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}
export function quoteChatVerificationStatus(
  scope: QuoteChatScope,
): "checking" | "ready" | "blocked" | "error" {
  return verification?.key === key(scope) ? verification.status : "checking";
}
export function retryQuoteChatVerification(): void {
  verification = undefined;
  publish();
}

/** One active credential partition; no persistent cache of tokens or actors. */
export async function ensureQuoteChatScope(
  scope: QuoteChatScope,
  signal?: AbortSignal,
): Promise<QuoteReadCredential> {
  for (let rotations = 0; rotations < 3; rotations++) {
    assertOwner(scope, signal);
    const credential = captureQuoteReadCredential(),
      mine = key(scope);
    if (verification?.key !== mine) {
      const entry: Verification = {
        key: mine,
        status: "checking",
        promise: Promise.resolve(),
      };
      verification = entry;
      entry.promise = (async () => {
        for (let attempt = 0; attempt < 3; attempt++) {
          if (!quoteReadCredentialCurrent(credential))
            throw new QuoteChatOwnershipError();
          try {
            const matched = await verifyQuoteReadIdentity(
              scope.identity,
              credential,
            );
            if (!matched) {
              entry.status = "blocked";
              throw new QuoteChatOwnershipError();
            }
            if (!quoteReadCredentialCurrent(credential))
              throw new QuoteChatOwnershipError();
            entry.status = "ready";
            publish();
            return;
          } catch (error) {
            if (
              error instanceof QuoteChatOwnershipError ||
              !quoteReadCredentialCurrent(credential)
            )
              throw error;
            if (attempt === 2) {
              entry.status = "error";
              publish();
              throw error;
            }
            await new Promise((resolve) =>
              setTimeout(resolve, attempt === 0 ? 500 : 1500),
            );
          }
        }
      })();
      publish();
    }
    try {
      await verification.promise;
    } catch (error) {
      assertOwner(scope, signal);
      if (mine !== key(scope)) continue;
      publish();
      throw error;
    }
    assertOwner(scope, signal);
    if (mine !== key(scope)) continue;
    if (!quoteChatScopeCurrent(scope)) throw new QuoteChatOwnershipError();
    return credential;
  }
  throw new QuoteChatOwnershipError();
}

function fixedHeaders(
  init: RequestInit | undefined,
  credential: QuoteReadCredential,
): Headers {
  const headers = new Headers(init?.headers);
  headers.delete("Authorization");
  headers.delete("X-Tenant-Id");
  headers.delete("X-User-Id");
  headers.delete("X-User-Role");
  if (credential.token)
    headers.set("Authorization", `Bearer ${credential.token}`);
  else {
    const [tenant, user, role] = JSON.parse(credential.fallback) as (
      | string
      | null
    )[];
    if (!tenant || !user || !role) throw new QuoteChatOwnershipError();
    headers.set("X-Tenant-Id", tenant);
    headers.set("X-User-Id", user);
    headers.set("X-User-Role", role);
  }
  return headers;
}
export async function quoteChatFetch(
  url: string,
  init: RequestInit | undefined,
  scope: QuoteChatScope,
): Promise<Response> {
  const signal = init?.signal ?? undefined;
  let credential = await ensureQuoteChatScope(scope, signal),
    ownerCredential = credentialKey();
  const dispatch = () => {
    assertOwner(scope, signal);
    if (
      !quoteReadCredentialCurrent(credential) ||
      ownerCredential !== credentialKey()
    )
      throw new QuoteChatOwnershipError();
    return fetch(url, { ...init, headers: fixedHeaders(init, credential) });
  };
  let response = await dispatch();
  assertOwner(scope, signal);
  if (response.status === 401) {
    // This reply cannot refresh, erase or redirect a later login.
    if (ownerCredential !== credentialKey())
      throw new QuoteChatOwnershipError();
    const renewed = await tryRefreshToken();
    assertOwner(scope, signal);
    if (!renewed) {
      if (ownerCredential === credentialKey()) {
        localStorage.removeItem("cnc.access_token");
        localStorage.removeItem("cnc.refresh_token");
        if (
          typeof window !== "undefined" &&
          !window.location.pathname.startsWith("/login")
        )
          window.location.assign("/login");
      }
      throw new Error("报价对话登录已失效，请重新登录");
    }
    if (captureQuoteReadCredential().token !== renewed)
      throw new QuoteChatOwnershipError();
    credential = await ensureQuoteChatScope(scope, signal);
    ownerCredential = credentialKey();
    response = await dispatch(); // one retry, before any accepted SSE frame
  }
  await ensureQuoteChatScope(scope, signal);
  return response;
}

// Demand records are only run/UI lifetimes, not worksheet handles or save queues.
const demands = new Map<object, QuoteChatScope>();
const demandListeners = new Set<() => void>();
const demandChanged = () => {
  for (const listener of demandListeners) listener();
};
export function retainQuoteChatScope(scope: QuoteChatScope): () => void {
  const lease = {};
  demands.set(lease, scope);
  demandChanged();
  return () => {
    if (demands.delete(lease)) demandChanged();
  };
}
export function quoteChatDemandCount(
  identity: QuoteReadIdentity | null,
): number {
  return identity
    ? [...demands.values()].filter(
        (scope) =>
          scope.identity.tenantId === identity.tenantId &&
          scope.identity.userId === identity.userId &&
          scope.identity.role === identity.role &&
          scope.owns(),
      ).length
    : 0;
}
export function subscribeQuoteChatDemand(listener: () => void): () => void {
  demandListeners.add(listener);
  return () => {
    demandListeners.delete(listener);
  };
}
export function quoteChatCredentialSnapshot(): string {
  return credentialKey();
}
